import { useCallback, useEffect, useState } from 'react';
import { useContext, createContext } from 'react';
import Web3 from 'web3';

import { RoomObjectType } from '../types/room';
import { SearchFilter } from '../types/search';

import { BASE_API_ENDPOINT } from '../constants';

import { useTargetContext } from './TargetContext';
import { useConfigurationContext } from './ConfigurationContext';
import { useUserContext } from './UserContext';

type CachedRooms = {
  [key: string]: RoomObjectType;
};

type CachedRoomIds = {
  [key: string]: string[];
};

interface RoomContextType {
  cachedRooms: CachedRooms;
  cachedRoomsByWalletAddress: CachedRoomIds;
  edit: (
    roomId: string,
    roomName: string,
    roomDescription: string,
    roomVisitorLimit: number,
    roomLockType: string,
    image?: Blob
  ) => Promise<{ result: boolean; error: string } | RoomObjectType>;
  move: (
    roomId: string,
    floorNumber: number,
    startX: number,
    startY: number,
    endX: number,
    endY: number
  ) => Promise<{ result: boolean; error: string } | RoomObjectType>;
  enter: (roomId: string) => Promise<Boolean>;
  getRoomsByFilters: (
    filter: SearchFilter,
    attributes: string,
    signal: AbortSignal
  ) => Promise<any>;
  getRoomsByWalletAddress: (
    walletAddress: string,
    signal: AbortSignal
  ) => Promise<RoomObjectType[]>;
  getCachedRoomsByWalletAddress: (walletAddress: string) => RoomObjectType[];
  getSuggestedRooms: (
    attributes: string,
    signal: AbortSignal
  ) => Promise<RoomObjectType[]>;
}

class RequestError extends Error {}

const RoomContext = createContext<RoomContextType>({
  cachedRooms: {},
  cachedRoomsByWalletAddress: {},
  edit: (
    roomId: string,
    roomName: string,
    roomDescription: string,
    roomVisitorLimit: number,
    roomLockType: string,
    image?: Blob
  ) => new Promise((resolve, reject) => false),
  move: (
    roomId: string,
    floorNumber: number,
    startX: number,
    startY: number,
    endX: number,
    endY: number
  ) => new Promise((resolve, reject) => false),
  enter: (roomId: string) => new Promise((resolve, reject) => false),
  getRoomsByFilters: (
    filter: SearchFilter,
    attributes: string,
    signal: AbortSignal
  ) => new Promise((resolve, reject) => []),
  getRoomsByWalletAddress: (walletAddress: string, signal: AbortSignal) =>
    new Promise((resolve, reject) => []),
  getCachedRoomsByWalletAddress: (walletAddress: string) => [],
  getSuggestedRooms: (attributes: string, signal: AbortSignal) =>
    new Promise((resolve, reject) => []),
});

export function RoomContextProvider({ children }: { children: any }) {
  const { target } = useTargetContext();
  const { signables } = useConfigurationContext();
  const { signMessage, wallet_address } = useUserContext();

  const [rooms, setRoomsinternal] = useState<CachedRooms>({});

  const [roomsByWalletAddress, setRoomsByWalletAddressinternal] =
    useState<CachedRoomIds>({});

  const cacheRooms = useCallback(async (newRooms: RoomObjectType[]) => {
    setRoomsinternal((rooms) =>
      newRooms.reduce(
        (roomMap, room) => ({ ...roomMap, [room.roomId]: room }),
        rooms
      )
    );
  }, []);

  const cacheRoomsByWalletAddress = useCallback(
    async (walletAddress: string, newRooms: RoomObjectType[]) => {
      setRoomsByWalletAddressinternal((roomsByWalletAddress) => ({
        ...roomsByWalletAddress,
        [walletAddress]: newRooms.map((x) => x.roomId),
      }));
    },
    []
  );

  const edit = useCallback(
    async (
      roomId: string,
      roomName: string,
      roomDescription: string,
      roomVisitorLimit: number,
      roomLockType: string,
      image?: Blob
    ) => {
      const timestamp = Date.now();
      const hashMessage = Web3.utils.sha3([roomId, timestamp].join('-')) || '';

      try {
        const signature = await signMessage(
          signables.editRoomSignMessage,
          hashMessage
        );

        const formData = new FormData();

        formData.append('roomName', roomName);
        formData.append('roomDescription', roomDescription);
        formData.append('roomVisitorLimit', roomVisitorLimit.toString());
        formData.append('roomLockType', roomLockType);

        if (image) {
          formData.append('image', image);
        }

        formData.append('signature', signature);
        formData.append('timestamp', timestamp.toString());
        try {
          const response = await fetch(
            `${BASE_API_ENDPOINT[target]}/explorer/${roomId}/edit/`,
            {
              method: 'POST',
              body: formData,
            }
          );

          if (!response.ok) {
            throw new RequestError(await response.text());
          }

          const room = await response.json();

          cacheRooms([room]);
          return room as RoomObjectType;
        } catch (err) {
          if (err instanceof RequestError && err.message) {
            return {
              result: false,
              error: err.message,
            };
          }

          return {
            result: false,
            error: 'Unknown network/server error',
          };
        }
      } catch (err) {
        target === 'dev' && console.log(err);
        return { result: false, error: 'Signature request was denied' };
      }
    },
    [cacheRooms, signMessage, signables.editRoomSignMessage, target]
  );

  const move = useCallback(
    async (
      roomId: string,
      floorNumber: number,
      startX: number,
      startY: number,
      endX: number,
      endY: number
    ) => {
      const timestamp = Date.now();
      const hashMessage = Web3.utils.sha3([roomId, timestamp].join('-')) || '';

      try {
        const signature = await signMessage(
          signables.moveRoomSignMessage,
          hashMessage
        );

        try {
          const response = await fetch(
            `${BASE_API_ENDPOINT[target]}/explorer/${roomId}/move/`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                floorNumber,
                startX,
                startY,
                endX,
                endY,
                signature,
                timestamp,
              }),
            }
          );

          if (!response.ok) {
            throw new RequestError(await response.text());
          }

          const room = await response.json();

          cacheRooms([room]);

          return room as RoomObjectType;
        } catch (err) {
          if (err instanceof RequestError && err.message) {
            return {
              result: false,
              error: err.message,
            };
          }

          return {
            result: false,
            error: 'Unknown network/server error',
          };
        }
      } catch (err) {
        target === 'dev' && console.log(err);
        return { result: false, error: 'Signature request was denied' };
      }
    },
    [cacheRooms, signMessage, signables.moveRoomSignMessage, target]
  );

  const enter = useCallback(
    async (roomId: string) => {
      const timestamp = Date.now();
      const hashMessage = Web3.utils.sha3([roomId, timestamp].join('-')) || '';

      try {
        const signature = await signMessage(
          signables.enterRoomSignMessage,
          hashMessage
        );

        const response = await fetch(
          `${BASE_API_ENDPOINT[target]}/explorer/${roomId}/enter/`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              timestamp,
              signature,
              walletAddress: wallet_address.toLowerCase(),
            }),
          }
        );

        if (!response.ok) {
          return false;
        }

        return true;
      } catch (err) {
        target === 'dev' && console.log(err);
        return false;
      }
    },
    [signMessage, signables.enterRoomSignMessage, target, wallet_address]
  );

  const getRoomsByFilters = useCallback(
    async (
      filter: SearchFilter,
      attributes: string,
      signal: AbortSignal
    ): Promise<RoomObjectType[]> => {
      const queryString = Object.keys(filter)
        .map((key) => {
          return key + '=' + filter[key as keyof SearchFilter];
        })
        .join('&');

      try {
        const response = await fetch(
          `${BASE_API_ENDPOINT[target]}/explorer/query?${queryString}&attributes=${attributes}&limit=15000&isPlaced=true`,
          {
            signal,
          }
        );

        if (!response.ok) {
          return [];
        }

        const rooms = await response.json();

        cacheRooms(rooms);

        return rooms;
      } catch (err) {
        return [];
      }
    },
    [cacheRooms, target]
  );

  const getRoomsByWalletAddress = useCallback(
    async (
      walletAddress: string,
      signal: AbortSignal
    ): Promise<RoomObjectType[]> => {
      try {
        const response = await fetch(
          `${BASE_API_ENDPOINT[target]}/account/tokens?account=${walletAddress}`,
          {
            signal,
          }
        );

        if (!response.ok) {
          return [];
        }

        const rooms = await response.json();

        cacheRooms(rooms);
        cacheRoomsByWalletAddress(walletAddress, rooms);

        return rooms;
      } catch (err) {
        if (signal.aborted) {
          throw new Error('Request cancelled.');
        }

        return [];
      }
    },
    [cacheRooms, cacheRoomsByWalletAddress, target]
  );

  const getCachedRoomsByWalletAddress = useCallback(
    (walletAddress: string): RoomObjectType[] => {
      const roomIds = roomsByWalletAddress[walletAddress];

      if (!roomIds) {
        return [];
      }

      const result = roomIds
        .map((roomId) => {
          return rooms[roomId] || null;
        })
        .filter(Boolean) as RoomObjectType[];

      return result;
    },
    [rooms, roomsByWalletAddress]
  );

  const getSuggestedRooms = useCallback(
    async (
      attributes: string,
      signal: AbortSignal
    ): Promise<RoomObjectType[]> => {
      try {
        const response = await fetch(
          `${BASE_API_ENDPOINT[target]}/explorer/suggested?attributes=${attributes}`,
          {
            signal,
          }
        );

        if (!response.ok) {
          return [];
        }

        const rooms = await response.json();

        cacheRooms(rooms);

        return rooms;
      } catch (err) {
        if (signal.aborted) {
          throw new Error('Request cancelled.');
        }

        return [];
      }
    },
    [cacheRooms, target]
  );

  return (
    <RoomContext.Provider
      value={{
        cachedRooms: rooms,
        cachedRoomsByWalletAddress: roomsByWalletAddress,
        move,
        edit,
        enter,
        getRoomsByFilters,
        getRoomsByWalletAddress,
        getCachedRoomsByWalletAddress,
        getSuggestedRooms,
      }}
    >
      {children}
    </RoomContext.Provider>
  );
}

export function useRoomContext() {
  const context = useContext(RoomContext);
  if (context === undefined) {
    throw new Error('Room context must be used within a Provider');
  }
  return context;
}

export function useRoomsByOwner(walletAddress: string) {
  const { getCachedRoomsByWalletAddress, getRoomsByWalletAddress } =
    useRoomContext();

  const [ids, setIds] = useState<RoomObjectType[]>(() =>
    getCachedRoomsByWalletAddress(walletAddress)
  );
  const [loading, setLoading] = useState(false);

  const [shouldRefresh, setShouldRefresh] = useState(true);

  const refresh = useCallback(() => {
    setShouldRefresh(true);
  }, []);

  useEffect(() => {
    if (!walletAddress) {
      return;
    }

    setShouldRefresh(true);
  }, [walletAddress]);

  useEffect(() => {
    if (!shouldRefresh || !walletAddress) {
      return;
    }

    setLoading(true);

    const abortController = new AbortController();

    getRoomsByWalletAddress(walletAddress, abortController.signal)
      .then(setIds)
      .catch(() => {})
      .finally(() => {
        setLoading(abortController.signal.aborted);
        setShouldRefresh(abortController.signal.aborted);
      });
    return () => abortController.abort();
  }, [getRoomsByWalletAddress, shouldRefresh, walletAddress]);

  useEffect(() => {
    setIds(getCachedRoomsByWalletAddress(walletAddress));
  }, [getCachedRoomsByWalletAddress, walletAddress]);

  return { rooms: ids, loading, refresh };
}
